import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button';

interface HamburgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Full-height drawer that slides in from the right with an opaque overlay.
 * Closes when a link (anchor) inside the content is clicked (navigation).
 */
export default function HamburgerMenu({ isOpen, onClose, children }: HamburgerMenuProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('a[href]')) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="hamburger-menu-overlay"
        role="button"
        tabIndex={-1}
        aria-label="Menüyü kapat"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Enter' && onClose()}
      />
      <div
        className="hamburger-menu-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Menü"
      >
        <div className="d-flex align-items-center justify-content-between px-3 py-3 border-bottom bg-white sticky-top">
          <span className="fw-bold text-primary">Menü</span>
          <Button
            type="button"
            className="btn btn-link text-dark p-2"
            onClick={onClose}
            aria-label="Menüyü kapat"
          >
            <i className="fas fa-times fa-lg" />
          </Button>
        </div>
        <div
          className="hamburger-menu-body overflow-auto px-3"
          onClick={handleContentClick}
        >
          {children}
        </div>
      </div>
      <style>
        {`
        .hamburger-menu-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1040;
          animation: hamburger-fadeIn 0.2s ease;
        }
        .hamburger-menu-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: min(320px, 100vw);
          background: #fff;
          z-index: 1050;
          box-shadow: -4px 0 20px rgba(0,0,0,0.15);
          animation: hamburger-slideIn 0.25s ease;
          display: flex;
          flex-direction: column;
        }
        .hamburger-menu-body {
          flex: 1;
          padding-top: 0.5rem;
        }
        .mobile-menu-section-content .dropdown-item,
        .mobile-menu-section-content a {
          display: block;
          padding: 0.5rem 0.75rem;
          color: #212529;
          text-decoration: none;
          border-radius: 6px;
          transition: background 0.2s;
        }
        .mobile-menu-section-content .dropdown-item:hover,
        .mobile-menu-section-content a:hover {
          background: #f0f7ff;
          color: #0d6efd;
        }
        .mobile-menu-section-content .dropdown-item i,
        .mobile-menu-section-content a i {
          margin-right: 0.5rem;
          width: 1.25rem;
          text-align: center;
        }
        @keyframes hamburger-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes hamburger-slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        `}
      </style>
    </>,
    document.body
  );
}
